import React from "react";
import { Link } from "react-router-dom";
import { CATEGORIES } from "@/data/config";
import { resolveServiceCta } from "@/lib/cta";
import { billingLabel } from "@/lib/billing";
import { resolvePricing } from "@/lib/pricing";

// Dark-first service card: black surface, white text, yellow highlights.
const ServiceCard = ({ service }) => {
  const cat = CATEGORIES.find((c) => c.slug === service.category);
  const cta = resolveServiceCta(service);
  const pricing = resolvePricing(service);
  const ctaLabel = cta.label || service.cta?.label;

  return (
    <article
      data-testid={`service-card-${service.slug}`}
      className="group relative flex flex-col justify-between p-6 lg:p-8 bg-black text-white border border-white/15 hover:border-[#FFC600] transition-colors duration-200"
    >
      <div>
        <div className="flex items-center justify-between mb-5">
          <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#FFC600]">
            {cat?.name}
          </span>
          <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/55">
            {pricing.engagementType}
          </span>
        </div>
        <h3 className="text-2xl lg:text-3xl font-black leading-tight tracking-tighter mb-3 text-white">
          {service.name}
        </h3>
        <p className="text-sm leading-relaxed text-white/70">
          {service.outcome}
        </p>
      </div>

      <div className="mt-8 pt-6 border-t border-white/15">
        <div className="flex items-end justify-between gap-4">
          <div>
            {pricing.showPublicPrice ? (
              <>
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/45">
                  Price
                </div>
                <div className="text-xl font-bold mt-1 text-white" data-testid={`service-card-price-${service.slug}`}>
                  {pricing.displayLabel}
                </div>
              </>
            ) : (
              <>
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#FFC600]">
                  The offer
                </div>
                <div className="text-sm font-semibold mt-1 text-white leading-snug max-w-[220px]" data-testid={`service-card-price-${service.slug}`}>
                  {pricing.displayLabel}
                </div>
              </>
            )}
            <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-white/45">
              {billingLabel(service.billing)}
            </div>
          </div>
          <Link
            to={`/services/${service.slug}`}
            data-testid={`service-card-view-${service.slug}`}
            className="text-sm font-semibold underline underline-offset-4 text-[#FFC600] hover:text-white shrink-0"
          >
            View details →
          </Link>
        </div>
        <div className="mt-5 flex gap-2 flex-wrap">
          {cta.isInternal ? (
            <Link
              to={cta.href}
              state={cta.state}
              data-testid={`service-card-cta-${service.slug}`}
              className="inline-flex items-center px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-2 bg-[#FFC600] text-black border-[#FFC600] hover:bg-transparent hover:text-[#FFC600] transition-colors"
            >
              {ctaLabel}
            </Link>
          ) : (
            <a
              href={cta.href}
              target={cta.target}
              rel="noreferrer"
              data-testid={`service-card-cta-${service.slug}`}
              className="inline-flex items-center px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-2 bg-[#FFC600] text-black border-[#FFC600] hover:bg-transparent hover:text-[#FFC600] transition-colors"
            >
              {ctaLabel}
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

export default ServiceCard;
